import React, { useCallback, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { client, decodeHtml } from '@Utils'
import { tocAuthorEndpoints } from '@URLS'
import { CourseNavItem } from './CourseNavItem'
import { CourseNavSubMenu } from './CourseNavSubMenu'
import { NavbarButton } from './NavbarButton'
import { ResizeHandle } from './ResizeHandle'
import { CoursesMenu } from './CoursesMenu'
import { AddChapterRow } from './AddChapterRow'
import { TocDeleteModal } from './TocDeleteModal'
import { tocCollisionDetection, computeMoveRequest } from './tocDnd'

function NavbarButtonPortal(props) {
  const [container, setContainer] = useState(() => document.getElementById('toc-button'))

  useEffect(() => {
    const handleNavbarReloaded = () => {
      setContainer(document.getElementById('toc-button'))
    }
    window.addEventListener('navbar-reloaded', handleNavbarReloaded)
    return () => window.removeEventListener('navbar-reloaded', handleNavbarReloaded)
  }, [])

  return container && createPortal(<NavbarButton {...props} />, container)
}

// Covers the drawer while a structure edit is saving so a second edit can't
// start against a tree that's about to be replaced. Every mutation rewrites
// the whole docbook from the version it loaded, so two in flight at once means
// the second save silently drops the first one's change.
function TocBusyOverlay() {
  const drawer = document.getElementById('left-drawer')
  if (!drawer) return null
  return createPortal(
    <div className="toc-busy-overlay" role="status" aria-label="Saving">
      <i className="fa-solid fa-spinner fa-spin fa-lg" aria-hidden="true" />
    </div>,
    drawer
  )
}

function showTopMessage(message) {
  if (window.$ && window.$.meDialog && window.$.meDialog.topMessage) {
    window.$.meDialog.topMessage(message, 4000, 'danger')
  } else {
    console.error(message)
  }
}

function findTocItem(items, id) {
  for (const item of items) {
    if (item.id === id) return item
    if (item.menuItems && item.menuItems.length > 0) {
      const found = findTocItem(item.menuItems, id)
      if (found) return found
    }
  }
  return null
}

function tocItemContainsPage(item, pageId) {
  if (!pageId) return false
  if (item.id === pageId) return true
  return (item.menuItems || []).some(child =>
    tocItemContainsPage(child, pageId)
  )
}

// Returns the parent id of the given node (null for top-level chapters,
// undefined when the node isn't in the tree)
function findTocParentId(items, id, parentId = null) {
  for (const item of items) {
    if (item.id === id) return parentId
    if (item.menuItems && item.menuItems.length > 0) {
      const found = findTocParentId(item.menuItems, id, item.id)
      if (found !== undefined) return found
    }
  }
  return undefined
}

export const App = ({
  coursenum,
  drawerKey,
  useLocalStorage,
  allowAuthorTools,
}) => {
  const [contents, setContents] = useState([])
  const navMenuRef = useRef(null)
  const [handlePosition, setHandlePosition] = useState(null)
  const [editingId, setEditingId] = useState(null)
  // mirrors the jStorage-backed author tools toggle (see author/scripts/tools.jsm)
  const [authorMode, setAuthorMode] = useState(() => {
    try {
      return !!(
        window.$ &&
        window.$.jStorage &&
        window.$.jStorage.get('options_advanced_edit_mode')
      )
    } catch (e) {
      return false
    }
  })
  let pathParts = location.pathname.split('/').filter(x => x !== '')
  let currentPage = pathParts[pathParts.length - 1]

  useEffect(() => {
    const handleToggle = e => setAuthorMode(!!(e.detail && e.detail.enabled))
    window.addEventListener('advanced-edit-mode-changed', handleToggle)
    return () =>
      window.removeEventListener('advanced-edit-mode-changed', handleToggle)
  }, [])

  const authorEditing = !!allowAuthorTools && authorMode

  // One gate for every structure edit (add/rename/delete/move): each one saves
  // a whole new docbook version built from the tree it loaded, so a second
  // request that starts before the first finishes overwrites it. The ref is
  // what actually blocks re-entry - two clicks in the same tick would both read
  // a stale `isBusy` from their render's closure. The state drives the UI.
  const [isBusy, setIsBusy] = useState(false)
  const busyRef = useRef(false)
  // opens the destination chapter after a cross-chapter move
  const [revealId, setRevealId] = useState(null)

  const runMutation = useCallback(async fn => {
    if (busyRef.current) return
    busyRef.current = true
    setIsBusy(true)
    // only the move that sets revealId should reveal: the submenu effect keys
    // off menuItems, which gets a new identity on every reload, so a stale
    // revealId would re-open that chapter after unrelated edits
    setRevealId(null)
    try {
      await fn()
    } finally {
      busyRef.current = false
      setIsBusy(false)
    }
  }, [])

  // reloadToC runs on mount and again after every structure edit, so the guard
  // lives with the fetch rather than in a single effect's cleanup: it keeps a
  // response that lands after the drawer unmounts from setting state
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // server call to get toc data
  const reloadToC = useCallback(async () => {
    try {
      let resp = await client.get(
        `/reader/web.get_table_of_contents/${coursenum}`
      )
      if (mountedRef.current) {
        setContents(resp.data.contents)
      }
    } catch (err) {
      if (mountedRef.current) {
        console.error(err)
      }
    }
  }, [coursenum])

  useEffect(() => {
    reloadToC()
  }, [reloadToC])

  const handleAddItem = useCallback(
    (targetId, position) =>
      runMutation(async () => {
        try {
          const resp = await client.post(
            tocAuthorEndpoints.addItem({ coursenum }),
            { target_id: targetId, position: position }
          )
          await reloadToC()
          // drop the new item straight into rename mode
          setEditingId(resp.data.id)
        } catch (err) {
          showTopMessage(
            (err.response && err.response.data && err.response.data.error) ||
              'Could not add the new item.'
          )
        }
      }),
    [coursenum, reloadToC, runMutation]
  )

  const handleRename = useCallback(
    (nodeId, title) =>
      runMutation(async () => {
        setEditingId(null)
        try {
          await client.post(tocAuthorEndpoints.renameItem({ coursenum }), {
            node_id: nodeId,
            title: title,
          })
          await reloadToC()
        } catch (err) {
          showTopMessage(
            (err.response && err.response.data && err.response.data.error) ||
              'Could not rename the item.'
          )
        }
      }),
    [coursenum, reloadToC, runMutation]
  )

  const handleCancelRename = useCallback(() => setEditingId(null), [])

  const handleStartRename = useCallback(id => setEditingId(id), [])

  const [deleteTarget, setDeleteTarget] = useState(null)

  const handleStartDelete = useCallback(
    (id, title) => setDeleteTarget({ id, title }),
    []
  )

  const handleCancelDelete = useCallback(() => setDeleteTarget(null), [])

  const handleConfirmDelete = useCallback(
    () =>
      runMutation(async () => {
        if (!deleteTarget) return
        const target = deleteTarget
        setDeleteTarget(null)
        try {
          const resp = await client.post(
            tocAuthorEndpoints.deleteItem({ coursenum }),
            { node_id: target.id }
          )
          // if the reader is showing a page inside the deleted subtree, leave it
          const deletedItem = findTocItem(contents, target.id)
          if (deletedItem && tocItemContainsPage(deletedItem, currentPage)) {
            window.location.href = resp.data.url
            return
          }
          await reloadToC()
        } catch (err) {
          showTopMessage(
            (err.response && err.response.data && err.response.data.error) ||
              'Could not delete the item.'
          )
        }
      }),
    [deleteTarget, contents, currentPage, coursenum, reloadToC, runMutation]
  )

  // drag-and-drop reordering (author tools only; see tocDnd.js for the rules)
  const [activeDrag, setActiveDrag] = useState(null)
  const [dropIndicator, setDropIndicator] = useState(null)

  // a 5px activation distance keeps plain clicks navigating/toggling
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleMoveItem = useCallback(
    ({ node_id, target_id, position }) =>
      runMutation(async () => {
        try {
          const resp = await client.post(
            tocAuthorEndpoints.moveItem({ coursenum }),
            { node_id, target_id, position }
          )
          // a cross-parent move changes the URLs of the whole moved subtree;
          // if the reader is showing one of those pages, follow it (like delete)
          const movedItem = findTocItem(contents, node_id)
          const oldParentId = findTocParentId(contents, node_id)
          const newParentId =
            position === 'inside'
              ? target_id
              : findTocParentId(contents, target_id)
          if (
            movedItem &&
            tocItemContainsPage(movedItem, currentPage) &&
            oldParentId !== newParentId
          ) {
            window.location.href = resp.data.url
            return
          }
          await reloadToC()
          setRevealId(node_id)
        } catch (err) {
          showTopMessage(
            (err.response && err.response.data && err.response.data.error) ||
              'Could not move the item.'
          )
        }
      }),
    [coursenum, contents, currentPage, reloadToC, runMutation]
  )

  const handleDragStart = useCallback(event => {
    setActiveDrag(event.active.data.current)
  }, [])

  const handleDragOver = useCallback(event => {
    const { active, over } = event
    if (!over) {
      setDropIndicator(null)
      return
    }
    const req = computeMoveRequest(
      active.data.current,
      over.data.current,
      active.rect.current.translated,
      over.rect
    )
    setDropIndicator(req ? { id: over.id, position: req.position } : null)
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null)
    setDropIndicator(null)
  }, [])

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event
      setActiveDrag(null)
      setDropIndicator(null)
      if (!over) return
      const req = computeMoveRequest(
        active.data.current,
        over.data.current,
        active.rect.current.translated,
        over.rect
      )
      if (req) handleMoveItem(req)
    },
    [handleMoveItem]
  )

  const leftDrawer = document.getElementById('left-drawer')
  const readerLayout = document.getElementById('reader_layout')
  const MIN_WIDTH = 250
  const MAX_WIDTH = 650
  const MOBILE_BREAKPOINT = 767
  const ICONBAR_WIDTH = 46

  const getConstrainedWidth = width => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return window.innerWidth - ICONBAR_WIDTH
    }
    return Math.max(MIN_WIDTH, Math.min(width, MAX_WIDTH))
  }

  const persistWidth = w => {
    try {
      localStorage.setItem(`leftDrawerWidth-${coursenum}`, String(w))
    } catch (e) {
      /* ignore */
    }
  }

  const applyWidth = w => {
    const constrainedWidth = getConstrainedWidth(w)
    leftDrawer.style.width = `${constrainedWidth}px`

    // Update handle position
    if (document.body.classList.contains('has-left-drawer')) {
      setHandlePosition(constrainedWidth - 3)
      // setHandlePosition(constrainedWidth - 20)

      if (readerLayout) {
        readerLayout.style.marginLeft = `${constrainedWidth}px`
      }
      const arrow = document.querySelector('.navigation_arrow_left')
      if (arrow) arrow.style.left = `${constrainedWidth + 20}px`
    } else {
      setHandlePosition(null)
      if (readerLayout) readerLayout.style.marginLeft = ''
    }

    if (constrainedWidth !== w) {
      persistWidth(constrainedWidth)
    }
  }

  const handleDrawerOpen = () => {
    const currentWidth = leftDrawer.offsetWidth
    if (
      !useLocalStorage &&
      !leftDrawer.classList.contains('left-drawer-shadow')
    ) {
      leftDrawer.classList.add('left-drawer-shadow')
    }
    applyWidth(currentWidth)

    const triggeredBy = event && event.detail && event.detail.triggeredBy
    if (triggeredBy !== 'clieck') {
      requestAnimationFrame(() => {
        const coursesMenuButton = document.querySelector('.CourseNavMenuButton')
        if (coursesMenuButton) {
          coursesMenuButton.focus()
        }
      })
    }
  }

  const handleDrawerClose = event => {
    const currentWidth = leftDrawer.offsetWidth
    setTimeout(() => {
      leftDrawer.classList.remove('left-drawer-shadow')
    }, 100)
    applyWidth(currentWidth)

    // Close the dropdown menu if it's open
    const dropdownToggle = document.querySelector('#navMenu')
    const navMenuContainer = navMenuRef.current

    if (dropdownToggle && navMenuContainer) {
      const isOpen =
        dropdownToggle.getAttribute('aria-expanded') === 'true' ||
        dropdownToggle.classList.contains('show') ||
        navMenuContainer.classList.contains('show')

      if (isOpen) {
        dropdownToggle.click() // Close the dropdown
      }
    }

    // Only restore focus if this wasn't a hover-triggered close
    const shouldRestoreFocus =
      event.detail && event.detail.restoreFocus !== false

    // Restore focus to button after re-render
    if (shouldRestoreFocus) {
      setTimeout(() => {
        const button = document.querySelector('#navMenu')
        if (button) {
          button.focus()
        }
      }, 50) // Adjust timing if needed
    }
  }

  useEffect(() => {
    const MOBILE_BREAKPOINT = 767
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT

    if (isMobile) {
      // handleDrawerClose({ detail: { restoreFocus: false } })
      window.dispatchEvent(
        new CustomEvent('close-left-drawer', {
          detail: { restoreFocus: false },
        })
      )
    } else {
      if (window.location.pathname.includes('/web/reader/')) {
        document.body.classList.add('has-left-drawer')
      }
    }
  }, [currentPage])

  // Initialize left drawer resizer
  useEffect(() => {
    if (!leftDrawer) return

    const handleWindowResize = () => {
      const currentWidth = leftDrawer.offsetWidth
      applyWidth(currentWidth)
    }

    // Apply saved width and set initial handle position
    try {
      const saved = localStorage.getItem(`leftDrawerWidth-${coursenum}`)
      if (saved) {
        const parsed = parseInt(saved, 10)
        if (!Number.isNaN(parsed)) {
          applyWidth(parsed)
        }
      } else {
        // Apply default width on first load
        applyWidth(leftDrawer.offsetWidth || 275)
      }
    } catch (e) {
      console.error('Non-critical error:', e)
    }

    // Set initial handle position if drawer is already open
    if (document.body.classList.contains('has-left-drawer')) {
      const currentWidth = leftDrawer.offsetWidth
      setHandlePosition(currentWidth - 3)
      // setHandlePosition(currentWidth - 20)
    }

    // Listen for drawer open/close events to show/hide handle
    window.addEventListener('open-left-drawer', handleDrawerOpen)
    window.addEventListener('close-left-drawer', handleDrawerClose)

    // Handle keyboard resize - don't update state to avoid losing focus
    const handleKeyboardResize = e => {
      const { newWidth } = e.detail
      const validated = getConstrainedWidth(newWidth)

      // Update DOM directly without triggering state change
      leftDrawer.style.width = `${validated}px`

      const leftHandle = leftDrawer.querySelector('.left-resize-handle')
      if (leftHandle) {
        leftHandle.style.left = `${validated - 3}px`
        // leftHandle.style.left = `${validated - 20}px`
      }

      if (readerLayout) {
        readerLayout.style.marginLeft = `${validated}px`
      }
      const arrow = document.querySelector('.navigation_arrow_left')
      if (arrow) arrow.style.left = `${validated + 20}px`

      persistWidth(validated)
    }

    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('keyboard-resize', handleKeyboardResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleWindowResize)
      window.removeEventListener('keyboard-resize', handleKeyboardResize)
      window.removeEventListener('open-left-drawer', handleDrawerOpen)
      window.removeEventListener('close-left-drawer', handleDrawerClose)
    }
  }, [coursenum])

  const handleMenuKeyNavigation = useCallback(e => {
    if (
      e.target.getAttribute('role') !== 'menuitem' || // Only handle navigation if the event originated from a menuitem
      e.key === ' ' || // let submenus handle  spacebar
      (e.key === 'Enter' && e.target.tagName === 'BUTTON') // let submenus handle enter if the target el is a button
    ) {
      return
    }

    // Get ALL menu items, including both links and buttons
    const allMenuItems = Array.from(
      (navMenuRef.current &&
        navMenuRef.current.querySelectorAll(
          'a[role="menuitem"], button[role="menuitem"]'
        )) ||
        []
    )

    // Filter to only focusable items
    const focusableMenuItems = allMenuItems.filter(item => {
      return item.tabIndex === 0 && !item.disabled
    })

    const currentElement = e.target
    const currentIndex = focusableMenuItems.indexOf(currentElement)

    if (currentIndex === -1) {
      // Current element not found in menu items
      return
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        e.stopPropagation()
        const nextIndex = (currentIndex + 1) % focusableMenuItems.length
        if (focusableMenuItems[nextIndex]) {
          focusableMenuItems[nextIndex].focus()
        }
        break
      }

      case 'ArrowUp': {
        e.preventDefault()
        e.stopPropagation()
        const prevIndex =
          currentIndex === 0 ? focusableMenuItems.length - 1 : currentIndex - 1
        if (focusableMenuItems[prevIndex]) {
          focusableMenuItems[prevIndex].focus()
        }
        break
      }

      case 'Escape': {
        e.preventDefault()
        e.stopPropagation()
        // Close dropdown and return focus to trigger
        const dropdownToggle = document.querySelector('#navMenu')
        if (dropdownToggle) {
          dropdownToggle.click()
          dropdownToggle.focus()
        }
        break
      }

      case 'Home':
        e.preventDefault()
        e.stopPropagation()
        if (focusableMenuItems[0]) {
          requestAnimationFrame(() => {
            focusableMenuItems[0].focus()
          })
        }
        break

      case 'End':
        e.preventDefault()
        e.stopPropagation()
        if (focusableMenuItems[focusableMenuItems.length - 1]) {
          requestAnimationFrame(() => {
            focusableMenuItems[focusableMenuItems.length - 1].focus()
          })
        }
        break
    }
  }, [])

  const startLeftResize = useCallback(
    evt => {
      const leftDrawer = document.getElementById('left-drawer')
      if (!leftDrawer) return

      evt.preventDefault()
      const isTouch = evt.type === 'touchstart'
      const startX = isTouch ? evt.touches[0].clientX : evt.pageX
      const startWidth = leftDrawer.offsetWidth

      const getConstrainedWidth = width => {
        if (window.innerWidth < MOBILE_BREAKPOINT) {
          return window.innerWidth
        }
        return Math.max(MIN_WIDTH, Math.min(width, MAX_WIDTH))
      }

      document.body.classList.add('resizing-left')

      const onMove = moveEvt => {
        const moveX = isTouch ? moveEvt.touches[0].clientX : moveEvt.pageX
        const delta = moveX - startX
        const newWidth = startWidth + delta
        const validated = getConstrainedWidth(newWidth)

        leftDrawer.style.width = `${validated}px`
        setHandlePosition(validated - 3)
        // setHandlePosition(validated - 20)

        const readerLayout = document.getElementById('reader_layout')
        if (readerLayout) {
          readerLayout.style.marginLeft = `${validated}px`
        }
        const arrow = document.querySelector('.navigation_arrow_left')
        if (arrow) arrow.style.left = `${validated + 20}px`
      }

      const onEnd = () => {
        document.body.removeEventListener(
          isTouch ? 'touchmove' : 'mousemove',
          onMove
        )
        document.body.removeEventListener(
          isTouch ? 'touchend' : 'mouseup',
          onEnd
        )
        setTimeout(() => {
          document.body.classList.remove('resizing-left')
        }, 100)

        try {
          localStorage.setItem(
            `leftDrawerWidth-${coursenum}`,
            String(leftDrawer.offsetWidth)
          )
        } catch (e) {
          /* ignore */
        }
      }

      document.body.addEventListener(
        isTouch ? 'touchmove' : 'mousemove',
        onMove
      )
      document.body.addEventListener(isTouch ? 'touchend' : 'mouseup', onEnd)
    },
    [coursenum]
  )

  function ResizeHandlePortal() {
    const leftDrawer = document.getElementById('left-drawer')
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT

    if (!leftDrawer || handlePosition === null || isMobile) return null

    return createPortal(
      <ResizeHandle
        position={handlePosition}
        onMouseDown={startLeftResize}
        onTouchStart={startLeftResize}
      />,
      leftDrawer
    )
  }

  useEffect(() => {
    const drawer = document.getElementById('left-drawer')
    if (!drawer) return

    const handleDrawerClick = () => {
      // Only lock if user actually clicks inside drawer content, not outside edges
      // (You can fine-tune this if you have non-interactive areas)
      window.dispatchEvent(new CustomEvent('lock-left-drawer'))
    }

    drawer.addEventListener('mousedown', handleDrawerClick)
    return () => drawer.removeEventListener('mousedown', handleDrawerClick)
  }, [])

  let standardPadding = 12

  return (
    <React.Fragment>
      <NavbarButtonPortal
        drawerKey={drawerKey}
        useLocalStorage={useLocalStorage}
      />
      <ResizeHandlePortal />
      <CoursesMenu coursenum={coursenum} />
      <DndContext
        sensors={dndSensors}
        collisionDetection={tocCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <ul
          ref={navMenuRef}
          onKeyDown={handleMenuKeyNavigation}
          role="menu"
          aria-label="Course navigation submenu"
          className={`ml-0 pl-0 mb-0${
            authorEditing ? ' toc-author-editing' : ''
          }${isBusy ? ' toc-busy' : ''}`}
          style={{ listStyleType: 'none', marginTop: '.75rem' }}
        >
          <CourseNavItem
            itemTitle="Title Page"
            paddingLeft={12}
            coursenum={coursenum}
            isLocked={false}
            currentPage={currentPage}
            itemId={undefined} // Title page typically doesn't have an ID
            depth={1}
            icon={
              <i
                className="fa-light fa-home fa-fw"
                style={{
                  marginRight: '8px',
                }}
                aria-hidden="true"
              />
            }
          />
          {contents.map(item => {
            return (
              <CourseNavSubMenu
                key={item.id}
                menuItems={item.menuItems}
                isLocked={item.isLocked}
                itemTitle={item.title}
                paddingLeft={standardPadding}
                coursenum={coursenum}
                currentPage={currentPage}
                parentIdStr={item.id}
                depth={1}
                itemId={item.id}
                tag={item.tag}
                rawTitle={item.rawTitle}
                authorEditing={authorEditing}
                editingId={editingId}
                onAddItem={handleAddItem}
                onStartRename={handleStartRename}
                onRename={handleRename}
                onCancelRename={handleCancelRename}
                onStartDelete={handleStartDelete}
                dropIndicator={dropIndicator}
                moveBusy={isBusy}
                revealId={revealId}
              />
            )
          })}
          {authorEditing && (
            <AddChapterRow onAdd={() => handleAddItem(null, 'after')} />
          )}
        </ul>
        {/* portaled to body: the drawer's overflow clipping would cut the chip off */}
        {createPortal(
          // default zIndex is 999 - the chip must clear the drawer (1010)
          <DragOverlay dropAnimation={null} zIndex={1060}>
            {activeDrag ? (
              <div className="toc-drag-overlay">
                <i
                  className="fa-solid fa-grip-dots-vertical"
                  aria-hidden="true"
                />
                <span>{decodeHtml(activeDrag.title)}</span>
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      {isBusy && <TocBusyOverlay />}
      <TocDeleteModal
        target={deleteTarget}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </React.Fragment>
  )
}
